import { useEffect, useRef } from "react";
import { ArrowDown, ArrowUpRight, Check } from "lucide-react";
import { CinemaScene, StudioWindow } from "./CinemaScenes";
import "./ScrollStory.css";

const STORIES = {
  unternehmen: ["VON DER IDEE ZUM AUFTRITT", [
    ["Ein Betrieb. Viele Stärken.", "Am Anfang steht das, was Ihr Unternehmen besonders macht."],
    ["Alles findet seinen Platz.", "Leistungen, Bilder und Kontakt werden zu einem klaren Gesamtbild."],
    ["Bereit für den ersten Eindruck.", "Ein eigenständiger Auftritt macht Ihr Angebot verständlich und erreichbar."],
  ]],
  landingpage: ["WENIGER ABLENKUNG. MEHR FOKUS.", [
    ["Ein Angebot braucht Raum.", "Die Gestaltung lenkt den Blick auf Ihre wichtigste Leistung."],
    ["Der Blick wird zum Interesse.", "Eine klare Botschaft und passende Bilder erklären, worum es geht."],
    ["Der nächste Schritt ist klar.", "Ein direkter Kontaktweg führt vom Angebot zur unverbindlichen Anfrage."],
  ]],
  relaunch: ["BESTAND WIRD ZUKUNFT", [
    ["Hier beginnt die Veränderung.", "Die bisherige Website ist unsere Ausgangslage. Gute Inhalte bleiben wertvoll."],
    ["Eine neue Perspektive entsteht.", "Struktur, Gestaltung und Bedienung werden gemeinsam weitergedacht."],
    ["Vertrauter Betrieb. Neuer Auftritt.", "Bewährtes bleibt erhalten und bekommt eine klare, zeitgemäße Form."],
  ]],
  berlin: ["VON BERLIN IN IHREN ALLTAG", [
    ["Hier beginnt die Zusammenarbeit.", "Persönlich aus Berlin. Mit einem Blick für Ihren Betrieb und Ihre Kunden."],
    ["Ihre Idee bekommt eine Form.", "Aus unserem Gespräch entsteht ein passender digitaler Auftritt."],
    ["Ihr Unternehmen wird sichtbar.", "Eine Website mit eigener Handschrift – in Berlin und darüber hinaus."],
  ]],
  salon: ["DEIN WEG ZUM NÄCHSTEN LOOK", [
    ["Ankommen. Durchatmen.", "Ein erster Einblick in die Atmosphäre unseres fiktiven Ateliers."],
    ["Dein Stil. Deine Auswahl.", "Entdecke Schnitt, Farbe und Styling in einer klaren Übersicht."],
    ["Ein Moment für dich.", "Probiere anschließend die Terminwahl aus. Sie simuliert eine Buchung, ohne einen echten Termin anzulegen."],
  ]],
};

function useScrollStory(ref, type) {
  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let frame = 0;
    let lastProgress;
    const clamp = (n) => Math.max(0, Math.min(1, n));
    const paint = () => {
      frame = 0;
      const box = root.getBoundingClientRect();
      const p = (motion.matches || document.documentElement.dataset.reduceMotion === "true") ? 1 : clamp(-box.top / Math.max(1, box.height - window.innerHeight));
      const reduced = String((motion.matches || document.documentElement.dataset.reduceMotion === "true"));
      if (root.dataset.reduced !== reduced) root.dataset.reduced = reduced;
      const value = p.toFixed(4);
      if (value === lastProgress) return;
      lastProgress = value;
      root.style.setProperty("--story-p", value);
      root.style.setProperty("--story-a", String(clamp((.38 - p) / .14)));
      root.style.setProperty("--story-b", String(Math.min(clamp((p - .23) / .14), clamp((.77 - p) / .14))));
      root.style.setProperty("--story-c", String(clamp((p - .62) / .14)));
    };
    const schedule = () => { if (!frame) frame = window.requestAnimationFrame(paint); };
    paint();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("damerow-motion-change", schedule);
    window.addEventListener("resize", schedule);
    motion.addEventListener("change", schedule);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("damerow-motion-change", schedule);
      window.removeEventListener("resize", schedule);
      motion.removeEventListener("change", schedule);
    };
  }, [ref, type]);
}

function SalonScene() {
  return <div className="ds-salon-scene" aria-hidden="true">
    <div className="ds-salon-photo"><img src="/images/demo/gallery-2.jpg" alt="" loading="lazy" decoding="async" /><span>ATELIER NOVA<br /><em>Ein Gefühl von dir.</em></span></div>
    <div className="ds-salon-services"><small>DEIN LOOK / DEINE WAHL</small><strong>Schnitt.<br /><em>Farbe. Charakter.</em></strong>{["Schnitt & Styling", "Farbe & Highlights", "Pflege & Finish"].map((s, i) => <div key={s}><span>0{i + 1}</span>{s}<ArrowUpRight size={17} /></div>)}</div>
    <div className="ds-salon-calendar"><small>TERMINWAHL / SIMULATION</small><strong>Zeit für<br /><em>etwas Neues.</em></strong><div className="ds-calendar-days">{["MO", "DI", "MI", "DO", "FR"].map((day, i) => <span key={day} className={i === 2 ? "selected" : ""}>{day}<b>{12 + i}</b></span>)}</div><div className="ds-calendar-time"><Check size={17} />10:30 / DEIN BEISPIELTERMIN</div><p>Fiktives Konzept. Kein echter Termin.</p></div>
  </div>;
}

export default function ScrollStory({ type }) {
  const ref = useRef(null);
  const [label, chapters] = STORIES[type];
  useScrollStory(ref, type);
  const next = type === "salon" ? "#demo-leistungen" : "#grundlagen";
  return <section className={`ds-journey ds-${type}`} id="scroll-story" ref={ref} data-testid={`scroll-story-${type}`} aria-label={label}>
    <div className="ds-pin">
      <div className="ds-heading"><span>{label}</span><a href={next} aria-label="Scroll-Geschichte überspringen">Weiter zum Inhalt <ArrowDown size={12} /></a></div>
      <div className="ds-visual" aria-hidden="true">
        <div className="ds-halo" />
        {type === "salon" ? <SalonScene /> : <CinemaScene type={type} />}
        {type === "berlin" && <div className="ds-berlin-site" style={{ "--story-photo": 'url("/images/hero-workspace.jpg")' }}><StudioWindow /></div>}
        {type === "unternehmen" && <><span className="ds-piece ds-piece-one">01 / IHRE LEISTUNGEN</span><span className="ds-piece ds-piece-two">02 / IHR CHARAKTER</span><span className="ds-piece ds-piece-three">03 / EIN DIREKTER KONTAKT</span></>}
      </div>
      <div className="ds-chapters">{chapters.map(([title, text], i) => <article className={`ds-chapter ds-chapter-${i}`} key={title}><span>0{i + 1} / 03</span><h2>{title}</h2><p>{text}</p></article>)}</div>
      <div className="ds-bottom"><span>{type === "salon" ? "FIKTIVES SALON-KONZEPT · KEINE ECHTE BUCHUNG" : "ILLUSTRATIVE GESTALTUNG · KEIN KUNDENPROJEKT"}</span><a href={next}>Weiter entdecken<ArrowDown size={15} /></a></div>
      <div className="ds-progress" aria-hidden="true"><i /></div>
    </div>
  </section>;
}
